import * as React from 'react';
import { Typography, Box, Container, Button, CircularProgress, MenuItem, List, ListItem, Snackbar, Checkbox } from '@mui/material';
import { connect } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { centered_flex_box, MainInput, MainPassword, main_button, sec_button } from '../../app/components/Styles';
import { isMobile } from 'react-device-detect';
import { addShippingServices, getFincartDefaultPickupLocation } from '../../app/store/actions/imsAdminActions';
import ShipBlu from "../../app/components/shipblu-webhooks.jpg"
import { Image, notification } from 'antd';
import copy from "clipboard-copy";

export const CreateShippingService = ({ addShippingServices, getFincartDefaultPickupLocation, user, isLoading }) => {

    const navigate = useNavigate();
    const [service, setService] = React.useState("Bosta");
    const [snackBar, setSnackBar] = React.useState(false);
    const [webhooksCompleted, setWebhooksCompleted] = React.useState(false);
    const [fincartKey, setFincartKey] = React.useState("");
    const [blinkKey, setBlinkKey] = React.useState("");
    const [blinkEndpoint, setBlinkEndpoint] = React.useState("");
    const [pickupLocationId, setPickupLocationId] = React.useState("");
    const [pickupLocationName, setPickupLocationName] = React.useState("");
    const [loadingPickupLocation, setLoadingPickupLocation] = React.useState(false);

    const handleGetDefaultPickupLocation = async () => {
        if (!fincartKey) {
            notification.error({
                message: "Enter your Fincart API key first",
            });
            return;
        }

        setLoadingPickupLocation(true);
        try {
            const data = await getFincartDefaultPickupLocation(fincartKey);
            setPickupLocationId(data.pickupLocationId);
            setPickupLocationName(data.location?.name || "");
            notification.success({
                message: "Default pickup location loaded",
                description: data.location?.name ? `${data.location.name} (${data.pickupLocationId})` : data.pickupLocationId,
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingPickupLocation(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        if ((service === "ShipBlu" || service === "Fincart") && !webhooksCompleted) {
            notification.error({
                message: "Webhooks not completed",
                description: "Please complete the webhooks setup before creating the shipping service",
            });
            return;
        }
        if (service === "Fincart" && !pickupLocationId) {
            notification.error({
                message: "Pickup location required",
                description: "Click Get Default Location after entering your Fincart API key",
            });
            return;
        }
        var shippingService = {
            service: data.get('service'),
            name: data.get('name'),
            key: data.get('key'),
        }
        if (service === "Fincart" && pickupLocationId) {
            shippingService.pickupLocationId = pickupLocationId;
        }
        if (service === "Blink") {
            shippingService.endpoint = blinkEndpoint;
            shippingService.key = blinkKey || data.get('key');
        }
        addShippingServices(shippingService, navigate);
    };

    if (isLoading) {
        return (
            <Container maxWidth="xs">
                <Box sx={{ ...centered_flex_box, minHeight: isMobile ? "100%" : "100vh", minWidth: "100%" }}>
                    <CircularProgress sx={{ color: "var(--secColor)" }} />
                </Box>
            </Container>
        )
    }

    const handleCopy = (link) => {
        copy(link);
        setSnackBar(true);
    };

    return (
        <Container maxWidth="xs">
            <Box
                sx={{
                    paddingTop: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Box sx={{ width: '100%', mt: 2 }}>
                    <Typography mt={2} variant='h4' textAlign={"center"} sx={{ color: "var(--secColor)" }}>Create Shipping Service</Typography>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        <MainInput
                            margin="normal"
                            required
                            fullWidth
                            id="service"
                            label="Service"
                            name="service"
                            value={service}
                            onChange={({ target }) => {
                                setService(target.value);
                                setWebhooksCompleted(false);
                                setPickupLocationId("");
                                setPickupLocationName("");
                                setBlinkKey("");
                                setBlinkEndpoint("");
                            }}
                            autoFocus
                            select
                        >
                            <MenuItem value="Bosta">Bosta</MenuItem>
                            <MenuItem value="ShipBlu">ShipBlu</MenuItem>
                            <MenuItem value="Fincart">Fincart</MenuItem>
                            <MenuItem value="Blink">Blink</MenuItem>
                        </MainInput>
                        <MainInput
                            margin="normal"
                            required
                            fullWidth
                            id="name"
                            label="Service Name"
                            name="name"
                            autoFocus
                        />
                        <MainPassword
                            margin="normal"
                            required
                            fullWidth
                            id="key"
                            label="Service API Key"
                            name="key"
                            value={service === "Fincart" ? fincartKey : service === "Blink" ? blinkKey : undefined}
                            onChange={service === "Fincart" ? ({ target }) => {
                                setFincartKey(target.value);
                                setPickupLocationId("");
                                setPickupLocationName("");
                            } : service === "Blink" ? ({ target }) => {
                                setBlinkKey(target.value);
                                setPickupLocationId("");
                                setPickupLocationName("");
                            } : undefined}
                            autoFocus
                        />
                        {service === "Fincart" && (
                            <>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                                    <MainInput
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="pickupLocationId"
                                        label="Pickup Location ID"
                                        name="pickupLocationId"
                                        value={pickupLocationId}
                                        onChange={({ target }) => setPickupLocationId(target.value)}
                                        helperText={pickupLocationName ? `Default: ${pickupLocationName}` : "Use Get Default Location after entering your API key"}
                                    />
                                </Box>
                                <Button
                                    type="button"
                                    fullWidth
                                    variant="outlined"
                                    disabled={loadingPickupLocation || !fincartKey}
                                    onClick={handleGetDefaultPickupLocation}
                                    sx={{ mb: 1, ...sec_button }}
                                >
                                    {loadingPickupLocation ? <CircularProgress size={22} sx={{ color: "var(--secColor)" }} /> : "Get Default Location"}
                                </Button>
                            </>
                        )}
                        {service === "Blink" && (
                            <MainInput
                                margin="normal"
                                required
                                fullWidth
                                id="endpoint"
                                label="Blink API Base URL"
                                name="endpoint"
                                value={blinkEndpoint}
                                onChange={({ target }) => setBlinkEndpoint(target.value)}
                                helperText="Example: https://api.your-blink-host.com"
                            />
                        )}
                        {service === "ShipBlu" && <>
                            <Typography mt={2} variant='h5' textAlign={"center"} sx={{ color: "var(--secColor)" }}>[Important] ShipBlu Webhooks</Typography>
                            <Image src={ShipBlu} alt="ShipBlu" />
                            <List >
                                <ListItem>
                                    <Typography><b>1. </b>Head to <a href="https://app.shipblu.com/en/merchant/integrations?tab=webhooks&page=1" target="_blank" rel="noreferrer">ShipBlu Webhooks</a> to start setting your webhooks</Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography><b>2. </b>Click on <b>Add Webhook</b></Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography>
                                        <b>3. </b>In the <b>Endpoint</b> input, enter <b>https://api.chainzsolutions.com/webhooks/shipping/shipblu</b> [<NavLink className={"a2"} onClick={() => handleCopy("https://api.chainzsolutions.com/webhooks/shipping/shipblu")}>Copy URL</NavLink>]
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography><b>4. </b>Select all events in the "Subscribed To" input</Typography>
                                </ListItem>
                                <ListItem>  
                                    <Typography><b>5. </b>Finally, in the <b>Headers</b> input, enter <b>{`{ "chainz-key": "62jxLdR0ONgw0fq3KaAG" }`}</b> [<NavLink className={"a2"} onClick={() => handleCopy('{ "chainz-key": "62jxLdR0ONgw0fq3KaAG" }')}>Copy Headers</NavLink>]</Typography> 
                                </ListItem>
                                <ListItem>
                                    <Typography><b>6. </b>Click on <b>Add</b></Typography>
                                </ListItem>
                            </List>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                <Checkbox required value={webhooksCompleted} onChange={({ target }) => setWebhooksCompleted(target.checked)} />
                                <Typography>I have completed the webhooks setup</Typography>
                            </Box>
                        </>}
                        {service === "Fincart" && <>
                            <Typography mt={2} variant='h5' textAlign={"center"} sx={{ color: "var(--secColor)" }}>[Important] Fincart Webhooks</Typography>
                            <List>
                                <ListItem>
                                    <Typography><b>1. </b>In the Fincart merchant portal, open the profile menu and select <b>Webhook Details</b></Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography>
                                        <b>2. </b>In the <b>Endpoint</b> input, enter <b>https://api.chainzsolutions.com/webhooks/shipping/fincart</b> [<NavLink className={"a2"} onClick={() => handleCopy("https://api.chainzsolutions.com/webhooks/shipping/fincart")}>Copy URL</NavLink>]
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography><b>3. </b>Set HTTP method to <b>POST</b></Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography><b>4. </b>In the <b>Secret Token</b> input, enter <b>63zxLdR0ONgw0fq5KfFG</b> [<NavLink className={"a2"} onClick={() => handleCopy("63zxLdR0ONgw0fq5KfFG")}>Copy Secret Token</NavLink>]</Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography><b>5. </b>Save the webhook settings in Fincart</Typography>
                                </ListItem>
                            </List>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                <Checkbox required value={webhooksCompleted} onChange={({ target }) => setWebhooksCompleted(target.checked)} />
                                <Typography>I have completed the webhooks setup</Typography>
                            </Box>
                        </>}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2, ...main_button }}
                        >
                            Create Shipping Service
                        </Button>
                    </Box>
                </Box>
            </Box >
            <Snackbar
                open={snackBar}
                autoHideDuration={3000}
                onClose={() => setSnackBar(false)}
                message="URL Copied"
            />
        </Container >
    );
}

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    isLoading: state?.records?.isLoading || state?.wait?.isLoading,
});

const mapDispatchToProps = { addShippingServices, getFincartDefaultPickupLocation };

export default connect(mapStateToProps, mapDispatchToProps)(CreateShippingService);
